import { watch } from 'vue'
import { useBrandStore } from '@/stores/brand'

const FONT_LINK_ID = 'shop-brand-font'
const WL_FONTS_LINK_ID = 'wl-template-fonts'
const FAVICON_ATTR = 'data-shop-brand-favicon'

/**
 * Side-effect installer: reactively mirrors the resolved shop brand onto
 * document-level surfaces (CSS variables, favicon, title, font).
 *
 * Mount-once from App.vue. Idempotent — calling install() twice is harmless.
 *
 * Only runs when a brand actually resolved. On the canonical Zaveato host
 * (where bootstrap short-circuits) brand stays null, so this composable
 * never touches the DOM and the existing useBranding flow keeps owning the
 * favicon/title — that's the isolation guarantee.
 */
export function useBrandThemeEffect() {
  const store = useBrandStore()

  function applyTheme(themeColor?: string | null, secondaryColor?: string | null) {
    const root = document.documentElement
    if (themeColor) {
      root.style.setProperty('--brand-theme-color', themeColor)
    } else {
      root.style.removeProperty('--brand-theme-color')
    }
    if (secondaryColor) {
      root.style.setProperty('--brand-secondary-color', secondaryColor)
    } else {
      root.style.removeProperty('--brand-secondary-color')
    }
  }

  /**
   * Build a colored-square SVG favicon with the shop's first letter as a
   * data URI. Used when a WL shop hasn't uploaded a custom favicon — keeps
   * the browser tab from showing the Zaveato Z on a custom-domain shop.
   * Returns a data: URL.
   */
  function generateBrandFavicon(shopName: string, themeColor: string | null | undefined): string {
    const initial = (shopName.trim().charAt(0) || 'S').toUpperCase()
    const bg = (themeColor && /^#[0-9a-fA-F]{3,8}$/.test(themeColor)) ? themeColor : '#0B5D34'
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">`
      + `<rect width="64" height="64" rx="14" fill="${bg}"/>`
      + `<text x="32" y="46" font-family="system-ui, -apple-system, 'Segoe UI', sans-serif" font-size="40" font-weight="800" text-anchor="middle" fill="#ffffff">${initial}</text>`
      + `</svg>`
    // URL-encode rather than base64 — slightly smaller and avoids extra deps.
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
  }

  function applyFavicon(faviconUrl: string | null | undefined, shopName: string, themeColor: string | null | undefined) {
    // On WL hosts the existing `<link rel="icon">` (and apple-touch-icon)
    // baked into index.html point at the Zaveato Z and MUST go — otherwise
    // the browser tab still shows the Zaveato favicon on a shop's custom
    // domain. Remove any link[rel="icon"|rel="apple-touch-icon"|rel="shortcut icon"]
    // we didn't add ourselves, then insert a single WL-tagged link.
    document.head
      .querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
      .forEach((el) => {
        if (!el.hasAttribute(FAVICON_ATTR)) el.remove()
      })

    // Replace any link we previously inserted so the brand can update live.
    const existing = document.querySelector(`link[${FAVICON_ATTR}]`)
    if (existing) existing.remove()

    const link = document.createElement('link')
    link.rel = 'icon'
    link.href = faviconUrl && faviconUrl.trim()
      ? faviconUrl
      : generateBrandFavicon(shopName, themeColor)
    link.setAttribute(FAVICON_ATTR, '')
    document.head.appendChild(link)
  }

  function applyTitle(shopName?: string) {
    if (shopName) {
      document.title = shopName
    }
  }

  function applyFont(fontFamily?: string | null) {
    // Remove any previously injected font link so switching brands doesn't pile
    // up stylesheets in the head.
    const existing = document.getElementById(FONT_LINK_ID)
    if (existing) existing.remove()

    if (!fontFamily) return

    // Conservative match: only load known-good Google-hosted families. Anything
    // else gets treated as a local family name and applied via CSS only.
    const safeFamilies = ['Inter', 'Poppins', 'Roboto', 'Montserrat', 'Lato', 'Open Sans', 'Playfair Display']
    if (safeFamilies.includes(fontFamily)) {
      const link = document.createElement('link')
      link.id = FONT_LINK_ID
      link.rel = 'stylesheet'
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@400;500;600;700&display=swap`
      document.head.appendChild(link)
    }

    document.documentElement.style.setProperty('--brand-font-family', `'${fontFamily}', system-ui, sans-serif`)
  }

  /**
   * The HalalFresh-derived WL template defaults to Playfair Display (headings)
   * + Poppins (body). Load both once when WL activates so the fallback chain
   * in the template's CSS vars actually resolves to a real webfont.
   */
  function ensureWlTemplateFonts() {
    if (document.getElementById(WL_FONTS_LINK_ID)) return
    const link = document.createElement('link')
    link.id = WL_FONTS_LINK_ID
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Poppins:wght@400;500;600;700;800&display=swap'
    document.head.appendChild(link)
  }

  function install() {
    watch(
      () => store.brand,
      (brand) => {
        // Default is always Zaveato. Per-shop branding (theme, favicon, title,
        // font, CSS vars, data-white-label attribute) is applied ONLY when the
        // current host is a fully verified white-label custom domain. Subdomain
        // visitors (mybusiness.zaveato.co.uk) and non-WL custom domains keep
        // the canonical Zaveato look and feel.
        if (!brand || !brand.isWhiteLabel) {
          document.documentElement.removeAttribute('data-white-label')
          applyTheme(null, null)
          applyFont(null)
          // Don't touch title/favicon — they may legitimately be Zaveato's, or
          // set by the standard SEO/useBranding flow which owns them off-WL.
          return
        }
        applyTheme(brand.themeColor, brand.secondaryColor)
        applyFavicon(brand.faviconUrl, brand.shopName, brand.themeColor)
        applyTitle(brand.shopName)
        applyFont(brand.fontFamily)
        document.documentElement.setAttribute('data-white-label', 'true')
        ensureWlTemplateFonts()
      },
      { immediate: true },
    )
  }

  return { install }
}
